import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface Video {
  id: string;
  title: string;
  description: string;
  source: string;
  source_url: string;
  thumbnail_url: string;
  duration_seconds: number;
  speaker: string;
  transcript_status: 'pending' | 'processing' | 'completed' | 'failed';
  chunk_count: number;
  published_at: string;
  category_ids: string[];
  created_at: string;
}

interface VideosResponse {
  data: Video[];
  meta: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

interface Category {
  id: string;
  name: string;
  slug: string;
  video_count: number;
}

type SortColumn =
  | 'title'
  | 'source_type'
  | 'duration_seconds'
  | 'published_at'
  | 'transcript_status'
  | 'chunk_count'
  | 'created_at';
type SortOrder = 'asc' | 'desc';

/**
 * Build a pagination button list with ellipses. Always shows first/last,
 * the current page, and one neighbor on each side. Returns 'ellipsis'
 * markers for the gaps.
 */
function getPageItems(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const items: Array<number | 'ellipsis'> = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) items.push('ellipsis');
  for (let i = left; i <= right; i++) items.push(i);
  if (right < total - 1) items.push('ellipsis');
  items.push(total);
  return items;
}

export function VideosPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortBy, setSortBy] = useState<SortColumn>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkCategoryIds, setBulkCategoryIds] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState<'add' | 'remove' | 'replace'>('add');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['videos', page, limit, sortBy, sortOrder],
    queryFn: () =>
      api.get<VideosResponse>(
        `/videos?page=${page}&per_page=${limit}&sort_by=${sortBy}&sort_order=${sortOrder}`
      ),
    // Auto-poll while any video on this page is still being processed so the
    // status badge updates without a manual refresh. Stops polling once every
    // visible video is in a terminal state.
    refetchInterval: (query) => {
      const items = query.state.data?.data || [];
      const inflight = items.some(
        (v) => v.transcript_status === 'pending' || v.transcript_status === 'processing'
      );
      return inflight ? 3000 : false;
    },
    refetchIntervalInBackground: false,
  });

  const { data: categoriesResp } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<{ data: Category[] }>('/categories'),
  });
  const categories = categoriesResp?.data || [];
  const categoryNameById = new Map(categories.map((c) => [c.id, c.name]));

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      // Sensible defaults: text columns ascend (A-Z), numeric/date columns descend.
      const ascendByDefault =
        column === 'title' || column === 'source_type' || column === 'transcript_status';
      setSortOrder(ascendByDefault ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const failedCount = data?.data?.filter((v) => v.transcript_status === 'failed').length || 0;

  const retryFailedMutation = useMutation({
    mutationFn: () =>
      api.post<{ data: { queued: number; message: string } }>(
        '/integrations/youtube/videos/retry-failed',
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: (input: {
      video_ids: string[];
      category_ids: string[];
      mode: 'add' | 'remove' | 'replace';
    }) => api.post<{ data: { updated: number } }>('/videos/bulk-update', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setSelectedIds(new Set());
      setBulkCategoryIds(new Set());
    },
  });

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const statusColors = {
    pending: 'bg-gray-100 text-gray-800',
    processing: 'bg-yellow-100 text-yellow-800 animate-pulse',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };

  const pageVideos = data?.data || [];
  const allOnPageSelected =
    pageVideos.length > 0 && pageVideos.every((v) => selectedIds.has(v.id));
  const someOnPageSelected = pageVideos.some((v) => selectedIds.has(v.id));

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const v of pageVideos) next.delete(v.id);
      } else {
        for (const v of pageVideos) next.add(v.id);
      }
      return next;
    });
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyBulkCategory = () => {
    if (selectedIds.size === 0) return;
    if (bulkMode !== 'replace' && bulkCategoryIds.size === 0) return;
    bulkUpdateMutation.mutate({
      video_ids: Array.from(selectedIds),
      category_ids: Array.from(bulkCategoryIds),
      mode: bulkMode,
    });
  };

  const toggleBulkCategory = (id: string) => {
    setBulkCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Videos</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your video content library
          </p>
        </div>
        <div className="flex gap-2">
          {failedCount > 0 && (
            <button
              onClick={() => retryFailedMutation.mutate()}
              disabled={retryFailedMutation.isPending}
              className="btn-secondary"
              title="Re-queue every failed YouTube video for caption download"
            >
              {retryFailedMutation.isPending
                ? 'Retrying...'
                : `Retry failed (${failedCount}+)`}
            </button>
          )}
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            Add Video
          </button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-blue-900">
              {selectedIds.size} video{selectedIds.size === 1 ? '' : 's'} selected
            </span>
            <span className="text-sm text-blue-700">·</span>
            <select
              value={bulkMode}
              onChange={(e) => setBulkMode(e.target.value as typeof bulkMode)}
              className="rounded border border-blue-300 bg-white px-2 py-1 text-sm"
            >
              <option value="add">Add categories</option>
              <option value="remove">Remove categories</option>
              <option value="replace">Replace categories with…</option>
            </select>
            <button
              onClick={applyBulkCategory}
              disabled={
                bulkUpdateMutation.isPending ||
                (bulkMode !== 'replace' && bulkCategoryIds.size === 0)
              }
              className="btn-primary"
            >
              {bulkUpdateMutation.isPending ? 'Applying…' : 'Apply'}
            </button>
            <button
              onClick={() => {
                setSelectedIds(new Set());
                setBulkCategoryIds(new Set());
              }}
              className="text-sm font-medium text-blue-700 hover:text-blue-900"
            >
              Clear
            </button>
          </div>

          {categories.length === 0 ? (
            <p className="text-xs text-blue-700">
              No categories defined yet. Create some on the Categories page first.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => {
                const checked = bulkCategoryIds.has(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleBulkCategory(c.id)}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border ${
                      checked
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-100'
                    }`}
                  >
                    {checked && <span className="leading-none">✓</span>}
                    {c.name}
                  </button>
                );
              })}
              {bulkMode === 'replace' && bulkCategoryIds.size === 0 && (
                <span className="text-xs text-blue-700 self-center">
                  (none selected — Apply will clear all categories from the selected videos)
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : data?.data?.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No videos yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Get started by adding your first video from YouTube, Vimeo, or upload directly.
          </p>
          <button onClick={() => setShowAddModal(true)} className="btn-primary mt-4">
            Add Your First Video
          </button>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected;
                      }}
                      onChange={toggleSelectAllOnPage}
                      className="rounded border-gray-300"
                      aria-label="Select all on this page"
                    />
                  </th>
                  <SortableHeader label="Video" column="title" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <SortableHeader label="Source" column="source_type" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Duration" column="duration_seconds" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Published" column="published_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Status" column="transcript_status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Chunks" column="chunk_count" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pageVideos.map((video) => {
                  const isSelected = selectedIds.has(video.id);
                  return (
                    <tr
                      key={video.id}
                      className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(video.id)}
                          className="rounded border-gray-300"
                          aria-label={`Select ${video.title}`}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          {video.thumbnail_url && (
                            <img
                              src={video.thumbnail_url}
                              alt=""
                              className="h-12 w-20 object-cover rounded"
                            />
                          )}
                          <div>
                            <Link
                              to={`/videos/${video.id}`}
                              className="font-medium text-gray-900 hover:text-primary-600"
                            >
                              {video.title}
                            </Link>
                            {video.speaker && (
                              <p className="text-sm text-gray-500">{video.speaker}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {video.category_ids && video.category_ids.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {video.category_ids.map((cid) => (
                              <span
                                key={cid}
                                className="inline-flex px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded"
                              >
                                {categoryNameById.get(cid) || cid}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 capitalize">
                        {video.source}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {video.duration_seconds ? formatDuration(video.duration_seconds) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {video.published_at
                          ? new Date(video.published_at).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                          : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${statusColors[video.transcript_status]}`}>
                          {video.transcript_status === 'processing' && (
                            <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                          )}
                          {video.transcript_status === 'processing' ? 'transcribing' : video.transcript_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {video.chunk_count}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={`/videos/${video.id}`}
                          className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data?.meta && data.meta.total > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span>
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, data.meta.total)} of {data.meta.total} videos
                </span>
                <span className="hidden sm:inline text-gray-300">·</span>
                <label className="hidden sm:inline-flex items-center gap-2">
                  <span>Show</span>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(parseInt(e.target.value, 10));
                      setPage(1);
                    }}
                    className="rounded border border-gray-300 px-2 py-1 text-sm"
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>per page</span>
                </label>
              </div>

              {data.meta.total_pages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {getPageItems(page, data.meta.total_pages).map((item, idx) =>
                    item === 'ellipsis' ? (
                      <span key={`e-${idx}`} className="px-2 text-gray-400">
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setPage(item)}
                        aria-current={item === page ? 'page' : undefined}
                        className={`min-w-[2.25rem] px-2 py-1 text-sm rounded border ${
                          item === page
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === data.meta.total_pages}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Add Video Modal */}
      {showAddModal && (
        <AddVideoModal
          categories={categories}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            queryClient.invalidateQueries({ queryKey: ['videos'] });
          }}
        />
      )}
    </div>
  );
}

interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  thumbnail_url: string;
  published_at: string;
  duration_seconds: number;
  has_captions: boolean;
}

interface YouTubeStatus {
  connected: boolean;
  channel?: {
    id: string;
    name: string;
    thumbnail_url: string;
    video_count: number;
  };
}

interface YouTubeVideosPage {
  data: YouTubeVideo[];
  pagination: { next_page_token?: string | null };
}

function CategoryMultiSelector({
  categories,
  value,
  onChange,
  label = 'Categories (optional)',
  helpText,
}: {
  categories: Category[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  helpText?: string;
}) {
  if (categories.length === 0) {
    return (
      <div>
        <label className="label">{label}</label>
        <p className="text-xs text-gray-500">
          No categories defined yet. Create some on the Categories page first.
        </p>
      </div>
    );
  }
  const toggle = (id: string) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };
  return (
    <div>
      <label className="label">{label}</label>
      <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
        {categories.map((c) => {
          const checked = value.includes(c.id);
          return (
            <label
              key={c.id}
              className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(c.id)}
                className="rounded border-gray-300"
              />
              <span>{c.name}</span>
            </label>
          );
        })}
      </div>
      {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
    </div>
  );
}

function AddVideoModal({
  categories,
  onClose,
  onSuccess,
}: {
  categories: Category[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [mode, setMode] = useState<'url' | 'import' | 'playlist'>('url');
  const [source, setSource] = useState<'youtube' | 'vimeo' | 'upload'>('youtube');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // YouTube import state
  const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set());
  const [importingVideos, setImportingVideos] = useState<Set<string>>(new Set());
  const [importedVideos, setImportedVideos] = useState<Set<string>>(new Set());

  // Check YouTube connection status
  const { data: youtubeStatus } = useQuery({
    queryKey: ['youtube-status'],
    queryFn: () => api.get<{ data: YouTubeStatus }>('/integrations/youtube/status'),
  });

  // Fetch YouTube videos when in import mode and connected — paginated.
  const {
    data: youtubeVideos,
    isLoading: loadingVideos,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['youtube-videos'],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '50' });
      if (pageParam) params.set('pageToken', pageParam);
      return api.get<YouTubeVideosPage>(`/integrations/youtube/videos?${params.toString()}`);
    },
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.pagination?.next_page_token || undefined,
    enabled: mode === 'import' && youtubeStatus?.data?.connected === true,
  });

  const flatYoutubeVideos: YouTubeVideo[] =
    youtubeVideos?.pages.flatMap((p) => p.data) || [];

  const mutation = useMutation({
    mutationFn: (data: {
      source: string;
      source_url: string;
      title?: string;
      category_ids?: string[];
    }) => api.post('/videos', data),
    onSuccess: () => onSuccess(),
    onError: (err: Error) => setError(err.message),
  });

  const importMutation = useMutation({
    mutationFn: (data: { video_id: string; category_ids?: string[] }) =>
      api.post<{ data: { id: string; title: string; has_transcript: boolean } }>(
        '/integrations/youtube/import',
        data
      ),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    await mutation.mutateAsync({
      source,
      source_url: url,
      title: title || undefined,
      category_ids: categoryIds.length > 0 ? categoryIds : undefined,
    });
    setIsSubmitting(false);
  };

  const handleImportSelected = async () => {
    setError('');
    const videoIds = Array.from(selectedVideos);

    for (const videoId of videoIds) {
      setImportingVideos(prev => new Set(prev).add(videoId));
      try {
        await importMutation.mutateAsync({
          video_id: videoId,
          category_ids: categoryIds.length > 0 ? categoryIds : undefined,
        });
        setImportedVideos(prev => new Set(prev).add(videoId));
      } catch (err: any) {
        setError(`Failed to import video: ${err.message}`);
      } finally {
        setImportingVideos(prev => {
          const next = new Set(prev);
          next.delete(videoId);
          return next;
        });
      }
    }

    // If all succeeded, close and refresh
    if (importedVideos.size + 1 === videoIds.length) {
      onSuccess();
    }
  };

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const isYouTubeConnected = youtubeStatus?.data?.connected === true;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-gray-600/75" onClick={onClose} />
      <div className={`relative bg-white rounded-xl shadow-xl mx-4 p-6 ${mode === 'import' || mode === 'playlist' ? 'max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col' : 'max-w-md w-full'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add Video</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMode('url')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              mode === 'url'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Add by URL
          </button>
          <button
            onClick={() => setMode('import')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2 ${
              mode === 'import'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <svg className="h-4 w-4 text-red-600" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            Import from YouTube
            {isYouTubeConnected && (
              <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                Connected
              </span>
            )}
          </button>
          <button
            onClick={() => setMode('playlist')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2 ${
              mode === 'playlist'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h12M19.5 17.25l-3 1.5v-3l3 1.5z" />
            </svg>
            Import Playlist
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {mode === 'url' ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Source</label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as typeof source)}
                className="input"
              >
                <option value="youtube">YouTube</option>
                <option value="vimeo">Vimeo</option>
                <option value="upload">Direct Upload</option>
              </select>
            </div>

            <div>
              <label className="label">
                {source === 'upload' ? 'Video URL' : `${source === 'youtube' ? 'YouTube' : 'Vimeo'} URL`}
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="input"
                placeholder={source === 'youtube' ? 'https://youtube.com/watch?v=...' : 'https://vimeo.com/...'}
                required
              />
            </div>

            <div>
              <label className="label">Title (optional)</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input"
                placeholder="Will be fetched from source if empty"
              />
            </div>

            <CategoryMultiSelector
              categories={categories}
              value={categoryIds}
              onChange={setCategoryIds}
              helpText="The new video will be assigned to all selected categories."
            />

            <div className="flex gap-3 justify-end pt-4">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Adding...
                  </>
                ) : (
                  'Add Video'
                )}
              </button>
            </div>
          </form>
        ) : !isYouTubeConnected ? (
          <div className="text-center py-8">
            <div className="mx-auto h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="h-6 w-6 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect YouTube</h3>
            <p className="text-sm text-gray-500 mb-4">
              Connect your YouTube channel to import videos with captions directly.
            </p>
            <a href="/settings" className="btn-primary inline-flex items-center gap-2">
              Go to Settings
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>
        ) : mode === 'playlist' ? (
          <PlaylistImportPanel
            categories={categories}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        ) : loadingVideos ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            <div className="mb-3">
              <CategoryMultiSelector
                categories={categories}
                value={categoryIds}
                onChange={setCategoryIds}
                helpText="All selected videos will be imported into the chosen categories."
              />
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="space-y-2">
                {flatYoutubeVideos.map((video) => {
                  const isSelected = selectedVideos.has(video.id);
                  const isImporting = importingVideos.has(video.id);
                  const isImported = importedVideos.has(video.id);

                  return (
                    <div
                      key={video.id}
                      onClick={() => {
                        if (isImported || isImporting) return;
                        setSelectedVideos(prev => {
                          const next = new Set(prev);
                          if (next.has(video.id)) {
                            next.delete(video.id);
                          } else {
                            next.add(video.id);
                          }
                          return next;
                        });
                      }}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isImported
                          ? 'bg-green-50 border-green-200 cursor-default'
                          : isSelected
                          ? 'bg-primary-50 border-primary-300'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex-shrink-0">
                        {isImported ? (
                          <div className="h-5 w-5 bg-green-500 rounded flex items-center justify-center">
                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                            </svg>
                          </div>
                        ) : isImporting ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <div className={`h-5 w-5 rounded border-2 ${isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'}`}>
                            {isSelected && (
                              <svg className="h-full w-full text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                              </svg>
                            )}
                          </div>
                        )}
                      </div>
                      <img
                        src={video.thumbnail_url}
                        alt=""
                        className="h-12 w-20 object-cover rounded flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{video.title}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{formatDuration(video.duration_seconds)}</span>
                          <span>•</span>
                          <span>{new Date(video.published_at).toLocaleDateString()}</span>
                          {video.has_captions && (
                            <>
                              <span>•</span>
                              <span className="text-green-600">Has captions</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {hasNextPage && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="btn-secondary"
                  >
                    {isFetchingNextPage ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Loading more...
                      </>
                    ) : (
                      'Load more videos'
                    )}
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-between pt-4 border-t mt-4">
              <p className="text-sm text-gray-500 self-center">
                {selectedVideos.size} video{selectedVideos.size !== 1 ? 's' : ''} selected
                {flatYoutubeVideos.length > 0 && (
                  <span className="ml-2 text-gray-400">
                    · {flatYoutubeVideos.length} loaded
                  </span>
                )}
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={handleImportSelected}
                  disabled={selectedVideos.size === 0 || importingVideos.size > 0}
                  className="btn-primary"
                >
                  {importingVideos.size > 0 ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Importing...
                    </>
                  ) : (
                    `Import ${selectedVideos.size} Video${selectedVideos.size !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SortableHeader({
  label,
  column,
  sortBy,
  sortOrder,
  onSort,
}: {
  label: string;
  column: SortColumn;
  sortBy: SortColumn;
  sortOrder: SortOrder;
  onSort: (column: SortColumn) => void;
}) {
  const active = sortBy === column;
  return (
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      <button
        type="button"
        onClick={() => onSort(column)}
        aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`group inline-flex items-center gap-1 select-none ${
          active ? 'text-gray-900' : 'hover:text-gray-700'
        }`}
      >
        {label}
        <span className={`text-[10px] leading-none ${active ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}>
          {active ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

interface PlaylistInfo {
  id: string;
  title: string;
  description: string;
  thumbnail_url: string;
  channel_title: string;
  item_count: number;
}

interface PlaylistPreviewResponse {
  data: { playlist: PlaylistInfo | null; videos: YouTubeVideo[] };
  pagination: { next_page_token: string | null };
}

function PlaylistImportPanel({
  categories,
  onClose,
  onSuccess,
}: {
  categories: Category[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [url, setUrl] = useState('');
  const [submittedUrl, setSubmittedUrl] = useState<string | null>(null);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    queued: number;
    skipped: number;
    title: string;
  } | null>(null);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['youtube-playlist', submittedUrl],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ url: submittedUrl! });
      if (pageParam) params.set('pageToken', pageParam);
      return api.get<PlaylistPreviewResponse>(
        `/integrations/youtube/playlist?${params.toString()}`
      );
    },
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.pagination?.next_page_token || undefined,
    enabled: !!submittedUrl,
    retry: false,
  });

  const playlist = data?.pages[0]?.data?.playlist;
  const videos: YouTubeVideo[] = data?.pages.flatMap((p) => p.data.videos) || [];

  const importMutation = useMutation({
    mutationFn: (vars: { url: string; category_ids?: string[] }) =>
      api.post<{
        data: {
          playlist: { id: string; title: string; item_count: number };
          queued: number;
          skipped: number;
          message: string;
        };
      }>('/integrations/youtube/playlist/import', vars),
  });

  const handlePreview = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setImportResult(null);
    if (!url.trim()) return;
    setSubmittedUrl(url.trim());
  };

  const handleImportAll = async () => {
    if (!submittedUrl) return;
    setError('');
    setImporting(true);
    try {
      const result = await importMutation.mutateAsync({
        url: submittedUrl,
        category_ids: categoryIds.length > 0 ? categoryIds : undefined,
      });
      setImportResult({
        queued: result.data.queued,
        skipped: result.data.skipped,
        title: result.data.playlist.title,
      });
      // Refresh the parent's videos list — pending rows will appear immediately.
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to import playlist');
    } finally {
      setImporting(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (importResult) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Playlist queued</h3>
        <p className="text-sm text-gray-600 mb-1">
          <span className="font-medium">{importResult.title}</span>
        </p>
        <p className="text-sm text-gray-500 mb-4">
          {importResult.queued} video{importResult.queued === 1 ? '' : 's'} queued for caption download
          {importResult.skipped > 0 && `, ${importResult.skipped} already imported`}.
        </p>
        <p className="text-xs text-gray-500 mb-6">
          Videos will appear in your library immediately as <span className="font-mono">pending</span>, and update to <span className="font-mono">completed</span> once captions are processed in the background.
        </p>
        <div className="flex gap-3 justify-center">
          <button onClick={onClose} className="btn-primary">Done</button>
        </div>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handlePreview} className="space-y-3 mb-4">
        <div>
          <label className="label">YouTube playlist URL</label>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="input"
            placeholder="https://www.youtube.com/playlist?list=PL..."
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Paste a playlist URL or ID. Every video in the playlist will be queued for caption download.
          </p>
        </div>

        <CategoryMultiSelector
          categories={categories}
          value={categoryIds}
          onChange={setCategoryIds}
          helpText="Every video in this playlist will be assigned to the chosen categories on import."
        />

        <div className="flex gap-2">
          <button type="submit" disabled={!url.trim() || isLoading} className="btn-secondary">
            {isLoading ? <><LoadingSpinner size="sm" /> Loading...</> : 'Preview'}
          </button>
        </div>
      </form>

      {submittedUrl && isLoading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {playlist && (
        <div className="flex-1 overflow-y-auto">
          <div className="flex items-start gap-3 p-3 mb-3 rounded-lg bg-gray-50 border">
            {playlist.thumbnail_url && (
              <img
                src={playlist.thumbnail_url}
                alt=""
                className="h-16 w-28 object-cover rounded flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 truncate">{playlist.title}</p>
              <p className="text-sm text-gray-500 truncate">{playlist.channel_title}</p>
              <p className="text-xs text-gray-500 mt-1">
                {playlist.item_count} video{playlist.item_count === 1 ? '' : 's'} in playlist
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {videos.map((video) => (
              <div
                key={video.id}
                className="flex items-center gap-3 p-2 rounded-lg border border-gray-200"
              >
                <img
                  src={video.thumbnail_url}
                  alt=""
                  className="h-10 w-16 object-cover rounded flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{video.title}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{formatDuration(video.duration_seconds)}</span>
                    {video.has_captions && (
                      <>
                        <span>•</span>
                        <span className="text-green-600">Has captions</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasNextPage && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="btn-secondary"
              >
                {isFetchingNextPage ? <><LoadingSpinner size="sm" /> Loading...</> : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}

      {playlist && (
        <div className="flex gap-3 justify-between pt-4 border-t mt-4">
          <p className="text-sm text-gray-500 self-center">
            {videos.length} loaded · {playlist.item_count} total
          </p>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleImportAll}
              disabled={importing}
              className="btn-primary"
            >
              {importing ? (
                <><LoadingSpinner size="sm" /> Queueing...</>
              ) : (
                `Import all ${playlist.item_count} video${playlist.item_count === 1 ? '' : 's'}`
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
